import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Server, Wifi, Shield, Code, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Services() {
  const services = [
    {
      id: 'it',
      icon: Server,
      title: 'IT Infrastructure Maintenance',
      subtitle: 'Keep your systems running at peak performance',
      description: 'Comprehensive hardware maintenance, system diagnostics, and proactive monitoring to ensure your IT infrastructure operates flawlessly.',
      image: 'https://images.unsplash.com/photo-1695668548342-c0c1ad479aee?w=800&q=80',
      features: [
        'Monthly preventive maintenance visits',
        'Hardware health monitoring',
        'System performance optimization',
        'Backup & disaster recovery setup',
        'Antivirus & security updates',
        '24/7 remote support'
      ],
      color: 'from-blue-600 to-cyan-400'
    },
    {
      id: 'networking',
      icon: Wifi,
      title: 'Networking Solutions',
      subtitle: 'Enterprise-grade connectivity infrastructure',
      description: 'Design, implement, and maintain robust network infrastructure that scales with your business needs.',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80',
      features: [
        'Network architecture design',
        'Firewall & security configuration',
        'VPN setup & management',
        'Bandwidth optimization',
        'Wi-Fi site surveys & deployment',
        'Network monitoring & alerts'
      ],
      color: 'from-blue-500 to-blue-300'
    },
    {
      id: 'cctv',
      icon: Shield,
      title: 'CCTV & Security Systems',
      subtitle: 'Advanced surveillance solutions',
      description: 'State-of-the-art security camera systems with intelligent monitoring, cloud storage, and real-time alerts.',
      image: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=800&q=80',
      features: [
        'HD/4K camera installation',
        'AI-powered motion detection',
        'Cloud & local storage options',
        'Mobile app remote viewing',
        'Night vision capabilities',
        'Quarterly maintenance visits'
      ],
      color: 'from-blue-600 to-cyan-500'
    },
    {
      id: 'software',
      icon: Code,
      title: 'Software Solutions',
      subtitle: 'Custom development & enterprise integrations',
      description: 'From custom software development to ERP integrations, we help digitize and streamline your business operations.',
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80',
      features: [
        'Custom application development',
        'ERP/CRM implementation',
        'Database management',
        'API integrations',
        'Legacy system modernization',
        'Ongoing support & updates'
      ],
      color: 'from-blue-500 to-cyan-400'
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-20" style={{ background: '#080B14' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="services-title">
            Our Services
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Comprehensive AMC solutions covering all aspects of your technology infrastructure. 
            One partner for all your IT needs.
          </p>
        </motion.div>

        {/* Services */}
        <div className="space-y-32">
          {services.map((service, idx) => {
            const Icon = service.icon;
            const isEven = idx % 2 === 0;

            return (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6 }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  isEven ? '' : 'lg:flex-row-reverse'
                }`}
                data-testid={`service-section-${service.id}`}
              >
                {/* Content */}
                <div className={isEven ? '' : 'lg:order-2'}>
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} mb-6 shadow-lg shadow-blue-500/20`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-2">{service.title}</h2>
                  <p className="text-blue-400 mb-4">{service.subtitle}</p>
                  <p className="text-gray-400 mb-8 leading-relaxed">{service.description}</p>

                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to="/pricing">
                    <Button className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:opacity-95 text-white font-semibold px-8 py-6 rounded-full hover:shadow-[0_0_30px_rgba(59,130,246,0.3)] shadow-[0_4px_12px_rgba(59,130,246,0.2)] border-none">
                      View Pricing
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>

                {/* Image */}
                <div className={isEven ? '' : 'lg:order-1'}>
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-400/20 rounded-2xl blur-3xl group-hover:blur-2xl transition-all duration-500 opacity-50" />
                    <div 
                      className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/80"
                      style={{ border: '1px solid rgba(99, 130, 255, 0.15)' }}
                    >
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500 opacity-90"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#080B14] via-transparent to-transparent opacity-60" />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-32 text-center"
        >
          <div className="glass-premium rounded-3xl p-12 border border-blue-500/10 shadow-2xl">
            <h2 className="text-3xl font-bold text-white mb-4">
              Not Sure Which Service You Need?
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Our experts will assess your infrastructure and recommend the perfect 
              combination of services for your business.
            </p>
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:opacity-95 text-white font-semibold px-8 py-6 rounded-full hover:shadow-[0_0_30px_rgba(59,130,246,0.3)] shadow-[0_4px_12px_rgba(59,130,246,0.2)] border-none">
                Get a Free Assessment
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
